/**
 * EXECUTOR-GAP-PANEL-1 (s199) — "What your executor still needs", promoted from a dashboard card to
 * its own page, with the gaps EDITABLE in place.
 *
 * Built to the ruled SPEC:
 * `docs/archive/working-notes/2026-08-30/session199-executor-still-needs-panel-SPEC-mockup.html`
 * (D-118.12 / D-193.1). The SPEC's own design notes state each ruling and its reason; this file
 * implements them and nothing beyond them.
 *
 * ## What is REUSED, not re-implemented
 *
 * Everything that already existed: the engine (`shared/planner-lens.ts`), its per-record grouping and
 * ranking, the tier vocabulary (`tierLabel`), the record-noun italic fallback, the row arrow's deep
 * link (`gapDeepLink`), the consequence copy (`gapSentence`), the counter math (`readyBlockedCounts`)
 * and — the whole reason this build is small — `EditableCell`, which is a controlled component
 * owning no write path of its own. Three things are new: the panel treatment, the two counters, and
 * the editor in the gap row.
 *
 * ## The two numbers (SPEC, "Two numbers, not one")
 *
 * The task universe does NOT move when a gap closes — the executor still has the same job, it just
 * got easier. `ready to go` is the number the planner's effort moves. Both come from
 * `readyBlockedCounts` in `shared/executor/task-counts.ts`, the ONE source the server also calls, and
 * the total is read live from the payload's `taskKeyUniverse` — never a literal.
 *
 * ## The save
 *
 * On blur, through the ONE existing planner write path (`POST /api/clients/:id/generic-entries`,
 * upserting on the `(client, field, row)` natural key). Registered **C-server-deduped** in
 * `single-flight-guard.test.ts` — the class every sibling call to this endpoint carries, VERIFIED
 * against `storage.upsertGenericEntry`, which looks the row up on that triple and updates in place.
 * (The build prompt suggested B-idempotent; the endpoint's own dedupe makes C the accurate class,
 * and the registry's existing entries for this endpoint all say C.)
 *
 * 🔴 G8 — this surface cannot overwrite planner data. A gap exists only where `isFieldResolvedInRow`
 * is false, i.e. no entry for that (field, row) holds a non-empty value and none is declined — and a
 * SEEDED value with content resolves the field exactly like a typed one (s109). So every write here
 * lands in a genuinely empty cell. There is no conflict field, and the D-168.6 opt-in does not arise.
 *
 * 🟢 Cat A cannot reach this surface at all. `isExcluded()` drops every Cat A ref inside the engine
 * (the value never reaches the server, so a gap for one could never be closed), and the server's
 * write-target resolution refuses Cat A a second time. A gap arriving without a `write` target
 * renders read-only with its deep link — this component never invents one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { AlertTriangle, ArrowRight, Check, ChevronRight } from "lucide-react";
import { EditableCell } from "@/components/EditableCell";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePlannerLens } from "@/hooks/usePlannerLens";
import { gapDeepLink, fieldKeyOf, tierLabel } from "@/lib/planner-lens-ui";
import { gapSentence } from "@shared/constants/consequence-copy";
import { deriveCellInputType } from "@shared/constants/section-layout-config";
import { readyBlockedCounts } from "@shared/executor/task-counts";
import type { Client, GenericField } from "@shared/schema";
import type { LensTier, PlannerGap, PlannerRecordGap } from "@shared/planner-lens";
import { SAGE, WARM } from "@shared/brand";

const RULE = "#E7E1D5";
const OK_BG = "#EDF3EA";

/** How long the "Saved" confirmation holds before the row leaves. Ruled 1.5s at s199 — 620ms was
 *  measured unreadable. */
const CONFIRM_HOLD_MS = 1500;
/** Must match the collapse transition below, so a row is removed only once it has finished. */
const COLLAPSE_MS = 430;

/** A gap's life after a successful save: hold the tick, collapse, then leave the list. */
type RowPhase = "saved" | "collapsing" | "gone";

/** Stable identity for one (record, field) gap instance. */
function gapKeyOf(rec: PlannerRecordGap, gap: PlannerGap): string {
  return `${rec.sectionSlug}::${rec.rowId ?? "__card__"}::${gap.ref}`;
}
function recordKeyOf(rec: PlannerRecordGap): string {
  return `${rec.sectionSlug}::${rec.rowId ?? "__card__"}`;
}

/** Critical reads as "do this first" (amber — red/rose is reserved for --destructive); every other
 *  tier is a quiet taupe chip. The same rule the dashboard card uses, so the two never disagree. */
function tierChipStyle(tier: LensTier): { color: string; backgroundColor: string } {
  return tier === "critical"
    ? { color: "#92400E", backgroundColor: "#FEF3C7" }
    : { color: WARM.taupe, backgroundColor: "#F5F2EC" };
}

/** Distinct copy per failure — one message for three causes is what turned `WIE-ERROR-COPY-1`'s
 *  investigation into a classification exercise instead of a diagnosis (phase 2b). */
export function saveStatusMessage(status: number): string {
  if (status === 403) return "You don't have permission to change this plan.";
  if (status === 404) return "That field is no longer part of your plan.";
  if (status === 409) return "Saving this here would clear other answers. Open the section to confirm.";
  return "Something went wrong saving that. Nothing was changed.";
}

/** Distinct copy per LOAD failure, same rule, different sentences — a plan that is gone, a plan that
 *  is not yours, and a server that broke are three different things to be told. */
export function loadStatusMessage(status: number | undefined): string {
  if (status === 404) return "This plan no longer exists. If you've just switched accounts, sign in again.";
  if (status === 403) return "You're signed in, but this plan isn't yours to see.";
  if (status === 500)
    return "Something broke on our side working this out. Nothing in your plan has changed — please try again shortly.";
  return "We couldn't reach the server. Check your connection and try again.";
}

/**
 * Hold the reader's place while a row leaves.
 *
 * Collapsing a row ABOVE the fold pulls everything up under the cursor; collapsing one at or below
 * it does not. So the compensation is measured and conditional rather than a blanket scroll restore:
 * note the row's height and whether it sat above the fold, and only then give the height back.
 */
function holdScrollPosition(el: HTMLElement | null): () => void {
  const scroller = document.getElementById("app-scroll-main");
  if (!el || !scroller) return () => {};
  const rect = el.getBoundingClientRect();
  const height = rect.height;
  const above = rect.bottom < scroller.getBoundingClientRect().top;
  return () => {
    if (above) scroller.scrollTop = Math.max(0, scroller.scrollTop - height);
  };
}

// ── one gap row ─────────────────────────────────────────────────────────────────────────────────

interface GapRowProps {
  clientId: string;
  rec: PlannerRecordGap;
  gap: PlannerGap;
  phase: RowPhase | undefined;
  error: string | undefined;
  onSaved: (key: string, gap: PlannerGap, rec: PlannerRecordGap, value: string, el: HTMLElement | null) => void;
  onFailed: (key: string, message: string) => void;
  onOptimistic: (key: string) => void;
}

function GapRow({ clientId, rec, gap, phase, error, onSaved, onFailed, onOptimistic }: GapRowProps) {
  const key = gapKeyOf(rec, gap);
  const [draft, setDraft] = useState("");
  const rowRef = useRef<HTMLDivElement>(null);
  // A discrete control (select / radio) fires onChange then onBlur inside one tick, so the blur
  // handler must read a synchronous mirror rather than the state it has only just queued — the
  // RADIO-RACE-1 shape (s154), which is why every other cell host keeps the same ref beside it.
  const draftRef = useRef("");
  const inFlight = useRef(false);

  const write = gap.write;
  const line = gapSentence(gap);

  /**
   * The write. `fetch` rather than `apiRequest` INSIDE the mutationFn, deliberately: the panel has to
   * tell 403, 404, 409 and 500 apart to say four different things (phase 2b), and `apiRequest`
   * collapses them into one thrown message. It is still a real `mutationFn`, so the single-flight
   * scanner sees a named mutation rather than a raw call it can neither guard nor exempt.
   *
   * Class **C-server-deduped** — `storage.upsertGenericEntry` looks the row up on the
   * `(clientId, fieldId, rowId)` triple and updates in place, so a second fire lands on the same row
   * with the same value. Registered under that class in `single-flight-guard.test.ts`.
   */
  const saveGapMutation = useMutation({
    mutationFn: async (value: string) => {
      const res = await fetch(`/api/clients/${clientId}/generic-entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          categoryId: write!.categoryId,
          sectionId: write!.sectionId,
          fieldId: write!.fieldId,
          rowId: rec.rowId,
          value,
        }),
      });
      // GATE-CLEAR-1 409: this value would close a showIf gate over non-empty siblings. The panel
      // deliberately does NOT offer the confirm — clearing another field's data is a decision that
      // belongs on the page able to show what is about to be cleared. Send the planner there.
      if (!res.ok) throw new Error(saveStatusMessage(res.status));
      return value;
    },
  });

  const save = useCallback(() => {
    const value = draftRef.current.trim();
    if (!write || !value || inFlight.current) return;
    inFlight.current = true;
    // Optimistic: the counters drop NOW, before the server answers. A failure rolls this back and
    // says so on the row — silence would leave the planner believing a fact is in their plan.
    onOptimistic(key);
    saveGapMutation.mutateAsync(value)
      .then(() => onSaved(key, gap, rec, value, rowRef.current))
      .catch((e: unknown) => onFailed(key, e instanceof Error ? e.message : saveStatusMessage(0)))
      .finally(() => {
        inFlight.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [write, rec, gap, key, onOptimistic, onSaved, onFailed]);

  if (phase === "gone") return null;

  const collapsing = phase === "collapsing";

  return (
    <div
      ref={rowRef}
      data-testid={`executor-needs-gap-${gap.ref}`}
      style={{
        display: "grid",
        gridTemplateRows: collapsing ? "0fr" : "1fr",
        opacity: collapsing ? 0 : 1,
        transition: `grid-template-rows ${COLLAPSE_MS}ms ease, opacity ${COLLAPSE_MS}ms ease`,
      }}
      className="motion-reduce:transition-none"
    >
      <div className="overflow-hidden">
        <div
          className="flex flex-wrap items-start gap-3 py-2.5 border-t border-dashed"
          style={{ borderColor: RULE, backgroundColor: phase ? OK_BG : undefined }}
        >
          <span className="text-[13px] font-semibold shrink-0" style={{ minWidth: "10.5rem" }}>
            {gap.label}
          </span>
          <span className="text-xs flex-1 min-w-[14rem] leading-snug" style={{ color: WARM.taupe }}>
            {line}
          </span>
          <div className="flex items-center gap-2.5 shrink-0">
            {phase ? (
              <span
                className="text-xs font-semibold rounded px-2.5 py-1 border"
                style={{ color: SAGE[600], borderColor: SAGE[600], backgroundColor: OK_BG }}
                data-testid={`executor-needs-saved-${gap.ref}`}
              >
                <Check className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" /> Saved
              </span>
            ) : write ? (
              // s199 (bundled): a native `date` input carries its own intrinsic width and does not
              // shrink to its container, so it rendered wider than — and offset from — the
              // `text`/`email` inputs above it. Fixing the COLUMN rather than the date branch keeps
              // every control type on one edge: the wrapper owns the width, and `w-full min-w-0`
              // makes each control fill it instead of asserting a width of its own. CSS only.
              <div className="w-[13rem] max-w-[46vw] shrink-0">
                <EditableCell
                  className="w-full min-w-0"
                  value={draft}
                  onChange={(v) => {
                    draftRef.current = v;
                    setDraft(v);
                  }}
                  onBlur={save}
                  type={deriveCellInputType(write.fieldType)}
                  options={write.options ?? undefined}
                  compact
                  showBorder
                  ariaLabel={gap.label}
                  testId={`executor-needs-input-${gap.ref}`}
                  field={{
                    id: write.fieldId,
                    name: gap.label,
                    fieldKey: fieldKeyOf(gap.ref),
                    fieldType: write.fieldType as GenericField["fieldType"],
                  }}
                />
              </div>
            ) : (
              // No write target — the field is not editable from here (not a live template field, or
              // one this surface must never write). The deep link still works.
              <Link
                href={gapDeepLink(gap, rec.rowId)}
                className="text-xs underline"
                style={{ color: SAGE[600] }}
                data-testid={`executor-needs-open-${gap.ref}`}
              >
                Open the section
              </Link>
            )}
            {/* 🔴 s199 — THE INPUT-ALIGNMENT FIX, and the second attempt at it.
                The first attempt widened the input COLUMN and forwarded a className to the date
                branch. Both were already correct, so it fixed nothing and shipped marked done.
                The variable was never the input: it is THIS CHIP. It is the last flex child of a
                `shrink-0` group, it has horizontal padding and NO width, and its label varies —
                `tierLabel` returns "Critical" (8) / "Important" (9) / "Routine" (7), uppercase with
                tracking. A wider chip makes the whole right-hand group wider, which pushes the
                fixed-width input LEFT. That is why the offset tracked the TIER rather than the
                field type, and why two Critical rows lined up with each other while an Important
                row sat ~10px off them.
                A fixed width plus centring makes every chip occupy identical space, so every input
                lands on the same edge whatever its tier or type. `tier-label-set` in the panel test
                pins the three labels this width was measured against, so a fourth tier cannot
                silently reintroduce the drift. */}
            <span
              data-testid={`executor-needs-tier-${gap.ref}`}
              className="shrink-0 w-[4.75rem] text-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
              style={tierChipStyle(gap.tier)}
            >
              {tierLabel(gap.tier)}
            </span>
          </div>
          {error && (
            <p
              className="w-full text-xs flex items-center gap-1.5"
              style={{ color: "#92400E" }}
              data-testid={`executor-needs-error-${gap.ref}`}
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── one record ──────────────────────────────────────────────────────────────────────────────────

function RecordBlock(props: {
  clientId: string;
  rec: PlannerRecordGap;
  label: string;
  defaultOpen: boolean;
  collapsing: boolean;
  phases: Record<string, RowPhase>;
  errors: Record<string, string>;
  onSaved: GapRowProps["onSaved"];
  onFailed: GapRowProps["onFailed"];
  onOptimistic: GapRowProps["onOptimistic"];
}) {
  const { clientId, rec, label, defaultOpen, collapsing, phases, errors } = props;
  const [open, setOpen] = useState(defaultOpen);
  const remaining = rec.fields.filter((g) => phases[gapKeyOf(rec, g)] !== "gone").length;

  return (
    <div
      data-testid={`executor-needs-record-${recordKeyOf(rec)}`}
      style={{
        display: "grid",
        gridTemplateRows: collapsing ? "0fr" : "1fr",
        opacity: collapsing ? 0 : 1,
        transition: `grid-template-rows ${COLLAPSE_MS}ms ease, opacity ${COLLAPSE_MS}ms ease`,
      }}
      className="motion-reduce:transition-none"
    >
      <div className="overflow-hidden">
        <div className="border-b" style={{ borderColor: RULE }}>
          <div className="flex items-center gap-3 w-full">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-3 flex-1 min-w-0 text-left py-3.5 px-1 hover:bg-[#F5F2EC]"
              data-testid={`executor-needs-record-toggle-${recordKeyOf(rec)}`}
            >
              <ChevronRight
                className="h-3.5 w-3.5 shrink-0 transition-transform motion-reduce:transition-none"
                style={{ color: WARM.taupe, transform: open ? "rotate(90deg)" : undefined }}
                aria-hidden="true"
              />
              {/* A record the planner hasn't identified yet reads its section's record noun in
                  italics — never a blank line they can't place (the s158 rule, kept). */}
              {label ? (
                <span className="text-base font-medium truncate">{label}</span>
              ) : (
                <span
                  className="text-base font-medium italic truncate"
                  style={{ color: WARM.taupe }}
                  data-testid={`executor-needs-record-fallback-${recordKeyOf(rec)}`}
                >
                  {rec.recordNoun.charAt(0).toUpperCase() + rec.recordNoun.slice(1)}
                </span>
              )}
              {rec.sectionName && rec.sectionName !== label && (
                <span
                  className="shrink-0 text-[11px] rounded-full px-2.5 py-0.5"
                  style={{ color: WARM.taupe, backgroundColor: "#F1EDE3" }}
                >
                  {rec.sectionName}
                </span>
              )}
              <span
                className="ml-auto shrink-0 text-xs whitespace-nowrap"
                style={{ color: WARM.taupe }}
                data-testid={`executor-needs-record-count-${recordKeyOf(rec)}`}
              >
                {remaining} missing
              </span>
            </button>
            <Link
              href={gapDeepLink(rec.fields[0], rec.rowId)}
              aria-label={`Open ${label || rec.recordNoun}`}
              className="shrink-0 pr-1"
              data-testid={`executor-needs-record-go-${recordKeyOf(rec)}`}
            >
              <ArrowRight className="h-4 w-4" style={{ color: SAGE[600] }} aria-hidden="true" />
            </Link>
          </div>

          {open && (
            <div className="pl-8 pr-1 pb-3">
              {rec.fields.map((gap) => (
                <GapRow
                  key={gapKeyOf(rec, gap)}
                  clientId={clientId}
                  rec={rec}
                  gap={gap}
                  phase={phases[gapKeyOf(rec, gap)]}
                  error={errors[gapKeyOf(rec, gap)]}
                  onSaved={props.onSaved}
                  onFailed={props.onFailed}
                  onOptimistic={props.onOptimistic}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── the panel ───────────────────────────────────────────────────────────────────────────────────

export function ExecutorNeedsPanel({ clientData }: { clientData: Client }) {
  const { data, isLoading, error } = usePlannerLens(clientData.id);
  const { toast } = useToast();
  const [phases, setPhases] = useState<Record<string, RowPhase>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // s199 bundled fix — filling the field that NAMES a record retitles it in place, by the SAME
  // `recordLabelFieldKey` rule the collapsed-row preview and the server-side lens both use. No third
  // notion of record identity is minted here, and `RECORD_FIELD_BY_SECTION` is untouched
  // (`RECORD-IDENTITY-1` is explicitly out of scope for this build).
  const [nameOverrides, setNameOverrides] = useState<Record<string, string>>({});
  const [collapsingRecords, setCollapsingRecords] = useState<Record<string, boolean>>({});
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);
  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  const records = useMemo(() => data?.records ?? [], [data]);

  const onOptimistic = useCallback((key: string) => {
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPhases((prev) => ({ ...prev, [key]: "saved" }));
  }, []);

  const onFailed = useCallback(
    (key: string, message: string) => {
      // Rollback: the row comes back, the counters go back up, and the reason is on the row.
      setPhases((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setErrors((prev) => ({ ...prev, [key]: message }));
      toast({ title: "Couldn't save that", description: message, variant: "destructive" });
    },
    [toast],
  );

  const onSaved = useCallback<GapRowProps["onSaved"]>(
    (key, gap, rec, value, el) => {
      // The record's name may have just been filled in.
      if (!rec.recordLabel && rec.rowId) {
        // 🔴 s199: the panel RECEIVES the resolved identity rather than deriving it. This is the one
        // surface with no field rows to read — it holds `PlannerRecordGap`, not `GenericField[]` —
        // and teaching it the rule would have meant importing the template spec into the browser
        // bundle. The server resolves; the record carries the answer.
        const identity = rec.identityFieldKey;
        if (identity && identity === fieldKeyOf(gap.ref)) {
          setNameOverrides((prev) => ({ ...prev, [recordKeyOf(rec)]: value }));
        }
      }
      // Hold the confirmation, then let the row go.
      later(() => {
        const restoreScroll = holdScrollPosition(el);
        setPhases((prev) => ({ ...prev, [key]: "collapsing" }));
        later(() => {
          setPhases((prev) => ({ ...prev, [key]: "gone" }));
          restoreScroll();
          // Refetch only once the row has left, so nothing re-renders under the cursor mid-animation.
          queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, "planner-lens"] });
          queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, "generic-entries"] });
          queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientData.id] });
        }, COLLAPSE_MS);
      }, CONFIRM_HOLD_MS);
    },
    [clientData.id, later],
  );

  // A record whose last field has gone follows it out.
  useEffect(() => {
    for (const rec of records) {
      const rk = recordKeyOf(rec);
      if (collapsingRecords[rk] || rec.fields.length === 0) continue;
      const anyLeft = rec.fields.some((g) => phases[gapKeyOf(rec, g)] !== "gone");
      if (!anyLeft) setCollapsingRecords((prev) => ({ ...prev, [rk]: true }));
    }
  }, [phases, records, collapsingRecords]);

  // ── counters ────────────────────────────────────────────────────────────────────────────────
  const openGaps = useMemo(
    () => records.flatMap((rec) => rec.fields.filter((g) => !phases[gapKeyOf(rec, g)])),
    [records, phases],
  );
  const counts = useMemo(
    () => readyBlockedCounts(data?.taskKeyUniverse ?? [], openGaps),
    [data?.taskKeyUniverse, openGaps],
  );
  const openRecords = useMemo(
    () => records.filter((rec) => rec.fields.some((g) => !phases[gapKeyOf(rec, g)])),
    [records, phases],
  );
  const criticalOpen = openGaps.filter((g) => g.tier === "critical").length;

  if (isLoading) {
    return (
      <p className="text-sm" style={{ color: WARM.taupe }} data-testid="executor-needs-loading">
        Loading…
      </p>
    );
  }
  if (error) {
    const status = (error as { status?: number }).status;
    return (
      <div
        className="rounded-lg border p-4 text-sm"
        style={{ borderColor: WARM.stone }}
        data-testid={`executor-needs-load-error-${status ?? "unknown"}`}
      >
        <p className="font-semibold mb-1">We couldn't load your executor's list.</p>
        <p style={{ color: WARM.taupe }}>{loadStatusMessage(status)}</p>
      </div>
    );
  }
  if (!data) return null;

  const allClear = openRecords.length === 0;

  return (
    <div data-testid="executor-needs-panel">
      {/* The two numbers. The total is read live from the payload and does NOT move as gaps close. */}
      <div
        className="flex flex-wrap items-baseline gap-x-11 gap-y-3 rounded-lg border p-5 mb-4"
        style={{ borderColor: WARM.stone, backgroundColor: "#FFFFFF" }}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-medium tabular-nums" data-testid="executor-needs-total">
            {counts.total}
          </span>
          <span className="text-[13px]" style={{ color: WARM.taupe }}>
            tasks for your executor
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span
            className="text-4xl font-medium tabular-nums"
            style={{ color: SAGE[600] }}
            data-testid="executor-needs-ready"
          >
            {counts.ready}
          </span>
          <span className="text-[13px]" style={{ color: WARM.taupe }}>
            ready to go
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-7 gap-y-2 pb-3.5 border-b" style={{ borderColor: RULE }}>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-medium tabular-nums" data-testid="executor-needs-detail-count">
            {openGaps.length}
          </span>
          <span className="text-[13px]" style={{ color: WARM.taupe }}>
            details missing
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-medium tabular-nums" data-testid="executor-needs-record-count">
            {openRecords.length}
          </span>
          <span className="text-[13px]" style={{ color: WARM.taupe }}>
            records
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-medium tabular-nums" data-testid="executor-needs-critical-count">
            {criticalOpen}
          </span>
          <span className="text-[13px]" style={{ color: WARM.taupe }}>
            critical
          </span>
        </div>
        <p className="ml-auto text-xs" style={{ color: WARM.taupe }}>
          Fill one in — press Tab or click away to save.
        </p>
      </div>

      {allClear ? (
        <div
          className="rounded-lg border p-10 text-center mt-4"
          style={{ borderColor: RULE, backgroundColor: "#FFFFFF" }}
          data-testid="executor-needs-empty"
        >
          <Check className="h-5 w-5 mx-auto mb-2" style={{ color: SAGE[600] }} aria-hidden="true" />
          <p className="text-lg font-medium mb-1">Nothing left to hunt for.</p>
          <p className="text-sm" style={{ color: WARM.taupe }}>
            Every stable fact your executor needs is in the plan.
          </p>
        </div>
      ) : (
        <div data-testid="executor-needs-list">
          {records.map((rec, i) => {
            const rk = recordKeyOf(rec);
            const allGone = rec.fields.length > 0 && rec.fields.every((g) => phases[gapKeyOf(rec, g)] === "gone");
            // Rendered while collapsing, dropped once the animation has run its course.
            if (allGone && !collapsingRecords[rk]) return null;
            return (
              <RecordBlock
                key={rk}
                clientId={clientData.id}
                rec={rec}
                label={nameOverrides[rk] ?? rec.recordLabel}
                defaultOpen={i === 0}
                collapsing={!!collapsingRecords[rk]}
                phases={phases}
                errors={errors}
                onSaved={onSaved}
                onFailed={onFailed}
                onOptimistic={onOptimistic}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
